/**
 * Typed client for the AUTHENTICATED half of klapzo-kit-api — saved agendas (US-MB-5) and the
 * method library as this tenant sees it (US-MB-6).
 *
 * Unlike `kitApi.ts` (which omits credentials on purpose, because `/public/*` is cacheable) every
 * call here sends the session cookie: agendas are tenant work product and there is no anonymous
 * equivalent to fall back to.
 *
 * A 401 is NOT an error here. `/kit/planner` is reachable without signing in — the planner has
 * always worked off a localStorage draft — so "no session" is an ordinary state the UI renders as
 * "your agenda lives in this browser", not a failure. Hence `null` rather than a throw.
 */
import type { AgendaBlock, AgendaBlockInput, Method, SharedAgendaWire } from "@klapzo/kit-core";

const KIT_API_BASE = (import.meta.env.VITE_KIT_API_BASE as string | undefined) ?? "https://kit-api.klapzo.app";

export interface AgendaSummary {
  id: string;
  title: string;
  startClock: string | null;
  totalMin: number;
  blockCount: number;
  createdAt: number;
  /** Whether a read-only link is live. The listing gets the boolean; only the full agenda has the token. */
  shared: boolean;
}

export interface Agenda extends AgendaSummary {
  /**
   * As the SERVER holds them (US-MB-6 fatia 2): `id` is `agenda_blocks.id`, minted server-side and
   * re-minted on every save, and `clientKey` is the uuid the planner sent. The draft keys on the
   * latter — see `draftBlocks`.
   */
  blocks: AgendaBlock[];
  /** Owner-only. Null when not shared. */
  shareToken: string | null;
}

/**
 * A saved agenda's blocks, back in the shape the DRAFT keys on (US-MB-6 fatia 2).
 *
 * The planner's whole state — the run badges, the results panel, and the `<blockId>.<8 hex>` round
 * ids it has already published to the room — hangs off one id per block, so opening a saved agenda
 * must restore the id it saved, not the row id the server minted this time round. That is
 * `clientKey`.
 *
 * `?? b.id` for a row written before 0009, or by a client that sent no key: it has no durable
 * identity to restore, so it borrows the server's for this session and picks up a key of its own on
 * the next save. Its old rounds stay unreachable — honestly, since nothing ever linked them.
 */
export function draftBlocks(blocks: readonly AgendaBlock[]): AgendaBlockInput[] {
  return blocks.map(({ clientKey, ...b }) => ({ ...b, id: clientKey ?? b.id }));
}

/**
 * A 4xx the server explained. `code` is its machine-readable `error` (today only
 * `INVALID_BLOCK_ID`), kept so the planner can say WHICH refusal it hit instead of a flat "save
 * failed" — and, more to the point, so nothing is tempted to "repair" the draft and retry: silently
 * re-minting a block id would detach every round that block already ran.
 */
export class AgendaRefusedError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "AgendaRefusedError";
  }
}

export interface AgendaInput {
  title: string;
  startClock: string | null;
  /**
   * WITH their ids, since US-MB-6 fatia 2: the uuid the planner minted goes up on every save and
   * comes back as `clientKey`, so a block keeps one identity and a closed round's `question_id`
   * still resolves to it. Stripping it — the `Omit<…, "id">` that used to live here, and its twin
   * in the store — is what dropped the join on the floor.
   */
  blocks: AgendaBlockInput[];
}

/**
 * `null` covers the two "nothing to do here" answers: **401** (no session — an ordinary state on
 * this screen) and **404** (the agenda is gone, e.g. deleted in another tab). Neither deserves an
 * error banner; callers fall back to the local draft or to creating a new row. Anything else
 * throws.
 */
async function call<T>(path: string, method: string, body?: unknown, signal?: AbortSignal): Promise<T | null> {
  const init: RequestInit = { method, credentials: "include" };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { "content-type": "application/json" };
  }
  if (signal) init.signal = signal;

  const res = await fetch(`${KIT_API_BASE}${path}`, init);
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) {
    // A 4xx carries a code the caller can act on; a 5xx carries nothing to trust. Reading the body
    // only under 500 keeps a proxy's HTML error page out of the message.
    const body = res.status < 500 ? ((await res.json().catch(() => null)) as { error?: unknown } | null) : null;
    if (typeof body?.error === "string") throw new AgendaRefusedError(body.error);
    throw new Error(`kit-api ${method} ${path} -> ${res.status}`);
  }
  return (await res.json()) as T;
}

const one = (id: string): string => `/agendas/${encodeURIComponent(id)}`;

export async function listAgendas(signal: AbortSignal): Promise<AgendaSummary[] | null> {
  const body = await call<{ agendas: AgendaSummary[] }>("/agendas", "GET", undefined, signal);
  return body && body.agendas;
}

export async function getAgenda(id: string): Promise<Agenda | null> {
  const body = await call<{ agenda: Agenda }>(one(id), "GET");
  return body && body.agenda;
}

export async function createAgenda(input: AgendaInput): Promise<Agenda | null> {
  const body = await call<{ agenda: Agenda }>("/agendas", "POST", input);
  return body && body.agenda;
}

/** PUT, because the server replaces the whole block list — that is the contract, not a shortcut. */
export async function updateAgenda(id: string, input: AgendaInput): Promise<Agenda | null> {
  const body = await call<{ agenda: Agenda }>(one(id), "PUT", input);
  return body && body.agenda;
}

export async function deleteAgenda(id: string): Promise<void> {
  await call<{ ok: true }>(one(id), "DELETE");
}

/** Idempotent — re-sharing returns the existing token, so a link already sent out keeps working. */
export async function shareAgenda(id: string): Promise<string | null> {
  const body = await call<{ shareToken: string }>(`${one(id)}/share`, "POST");
  return body && body.shareToken;
}

export async function unshareAgenda(id: string): Promise<void> {
  await call<{ ok: true }>(`${one(id)}/share`, "DELETE");
}

/**
 * The read-only view behind a share link: no id, no tenant, no owner. Unauthenticated — the token
 * is the authorisation — so this is the one agenda call that must NOT send the session cookie.
 */
export type SharedAgenda = SharedAgendaWire;

/**
 * ONE method by id — what the planner reads a block's `roundSpec` from.
 *
 * By id and one at a time, not `GET /methods`: the planner needs `roundSpec` for the handful of
 * methods its own blocks reference, and the list route ships the whole library (~190 KB of
 * `bodyMd`, `private, no-store`, so re-fetched on every mount) to read one field off a few rows
 * (round-1 review, M3).
 *
 * NOT `kitApi.getMethod` either: that one is keyed by SLUG on the public library, which RESOLVES
 * one row per locale — a block added while the library was in pt-BR would miss in an en-US
 * planner, and a tenant's own method is not there at all. `null` = signed out or no such method,
 * like every call here.
 */
export async function getVisibleMethod(id: string, signal?: AbortSignal): Promise<Method | null> {
  const body = await call<{ method: Method }>(`/methods/${encodeURIComponent(id)}`, "GET", undefined, signal);
  return body && body.method;
}

export async function getSharedAgenda(token: string, signal: AbortSignal): Promise<SharedAgenda | null> {
  const res = await fetch(`${KIT_API_BASE}/public/agendas/${encodeURIComponent(token)}`, {
    credentials: "omit",
    signal,
  });
  if (res.status === 404) return null; // unknown or revoked — the same answer on purpose
  if (!res.ok) throw new Error(`kit-api /public/agendas -> ${res.status}`);
  return ((await res.json()) as { agenda: SharedAgenda }).agenda;
}
